import React from "react";
import { Link } from "react-router-dom";
import { LogOut, Star, IdCard, Bookmark, Compass, Radio, ArrowRight } from "lucide-react";
import { desk, fontMicro, fontMono } from "./tokens";

const ClassifiedStamp = ({ isFounding }) => (
  <div
    className={`${fontMicro} inline-flex items-center gap-1.5 border px-2 py-1`}
    style={{
      color: isFounding ? desk.gold : desk.redact,
      borderColor: (isFounding ? desk.gold : desk.redact) + "55",
      backgroundColor: "rgba(0,0,0,0.4)",
      letterSpacing: "0.35em",
    }}
    data-testid="desk-classified-stamp"
  >
    <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse"
          style={{ backgroundColor: isFounding ? desk.gold : desk.redact }} />
    {isFounding ? "// FOUNDING OPERATIVE · EYES ONLY" : "// AUTHENTICATED · EYES ONLY"}
  </div>
);

const DeskHeader = ({ identity, clearance, stats, onLogout }) => {
  const isFounding = !!identity?.is_founding;
  const joined = (identity?.joined_at || "").slice(0, 10);
  const callsign = identity?.callsign || "OPERATIVE-UNKNOWN";
  const following = Number(stats?.cases_following || 0);
  const bookmarks = Number(stats?.bookmarks || 0);
  const unread = Number(stats?.unread_updates || 0);

  return (
    <header
      className="relative border p-5 sm:p-7 mb-6 overflow-hidden"
      style={{
        borderColor: isFounding ? desk.gold + "55" : desk.edge,
        backgroundColor: desk.surface,
        boxShadow: isFounding
          ? "inset 0 0 0 1px rgba(224,165,38,0.10), 0 0 40px rgba(224,165,38,0.04)"
          : "inset 0 0 0 1px rgba(255,255,255,0.02)",
      }}
      data-testid="member-desk-header"
    >
      {/* corner crosshairs */}
      <span aria-hidden className="absolute top-2 left-2 w-3 h-3 border-l border-t" style={{ borderColor: desk.edgeHi }} />
      <span aria-hidden className="absolute top-2 right-2 w-3 h-3 border-r border-t" style={{ borderColor: desk.edgeHi }} />
      <span aria-hidden className="absolute bottom-2 left-2 w-3 h-3 border-l border-b" style={{ borderColor: desk.edgeHi }} />
      <span aria-hidden className="absolute bottom-2 right-2 w-3 h-3 border-r border-b" style={{ borderColor: desk.edgeHi }} />

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <ClassifiedStamp isFounding={isFounding} />
          <div className={`${fontMono} mt-3`} style={{ color: desk.inkDim }}>
            THE ARCHIVE
          </div>
          <div className={`${fontMono} mb-3`} style={{ color: desk.redact, letterSpacing: "0.4em" }}>
            MEMBER DESK
          </div>
          <div className={`${fontMicro} mb-1`} style={{ color: desk.inkMuted }}>
            Welcome back, Investigator
          </div>
          <h1
            className="font-display text-2xl sm:text-4xl md:text-5xl leading-none"
            style={{ color: isFounding ? desk.gold : desk.ink, letterSpacing: "0.05em",
                     wordBreak: "keep-all", overflowWrap: "normal" }}
            data-testid="desk-callsign"
          >
            {callsign}
          </h1>
          <div className={`${fontMicro} mt-3 flex flex-wrap items-center gap-x-4 gap-y-1`}
               style={{ color: desk.inkDim }}>
            <span data-testid="desk-joined">JOINED {joined || "—"}</span>
            <span aria-hidden style={{ color: desk.edgeHi }}>·</span>
            <span data-testid="desk-tier">TIER · ARCHIVE MEMBER</span>
            {isFounding && (
              <>
                <span aria-hidden style={{ color: desk.edgeHi }}>·</span>
                <span style={{ color: desk.gold }} data-testid="desk-founding-flag">FOUNDING MEMBER</span>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-row sm:flex-col items-start sm:items-end gap-3 shrink-0 flex-wrap">
          <ClearanceEmblem clearance={clearance} />
          <div className="flex items-center gap-2 flex-wrap sm:justify-end">
            <Link
              to="/operative-card"
              data-testid="open-operative-card"
              className="inline-flex items-center gap-1.5 border px-2.5 py-1.5"
              style={{ borderColor: isFounding ? desk.gold + "88" : desk.classified + "88",
                       color: isFounding ? desk.gold : desk.classified }}
            >
              <IdCard size={11} />
              <span className={fontMicro}>OPERATIVE CARD</span>
            </Link>
            <button
              type="button"
              onClick={onLogout}
              data-testid="member-logout"
              className="inline-flex items-center gap-1.5 border px-2.5 py-1.5"
              style={{ borderColor: desk.edgeHi, color: desk.inkDim }}
            >
              <LogOut size={11} />
              <span className={fontMicro}>SIGN OUT</span>
            </button>
          </div>
        </div>
      </div>

      {/* ─── Investigator stats strip ────────────────────────────────── */}
      <div className="mt-6 border-t pt-4 flex flex-wrap items-end gap-4 sm:gap-6"
           style={{ borderColor: desk.edge }}
           data-testid="desk-stats-strip">
        <StatTile icon={<Compass size={12} />} label="DOSSIERS FOLLOWING"
                  value={following} testid="stat-following" />
        <StatTile icon={<Bookmark size={12} />} label="SAVED FILES"
                  value={bookmarks} testid="stat-bookmarks" />
        <StatTile icon={<Radio size={12} />} label="UNREAD ARCHIVE UPDATES"
                  value={unread}
                  accent={unread > 0}
                  testid="stat-unread" />
        <div className="flex-1 min-w-[180px] flex items-end justify-end">
          <Link
            to="/archive"
            data-testid="desk-continue-investigation"
            className="inline-flex items-center gap-2 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.3em]"
            style={{
              background: unread > 0 ? "#D92D20" : "transparent",
              color: unread > 0 ? "#FFFFFF" : desk.classified,
              border: `1px solid ${unread > 0 ? "#D92D20" : desk.classified}`,
            }}
          >
            CONTINUE INVESTIGATION
            <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    </header>
  );
};

const StatTile = ({ icon, label, value, accent = false, testid }) => (
  <div className="flex flex-col gap-1 min-w-[110px]" data-testid={testid}>
    <div className={`${fontMicro} flex items-center gap-1.5`}
         style={{ color: desk.inkMuted }}>
      {icon}
      {label}
    </div>
    <div
      className="font-display text-3xl leading-none"
      style={{ color: accent ? "#E0A526" : desk.ink }}
    >
      {value}
    </div>
  </div>
);

const ClearanceEmblem = ({ clearance }) => {
  if (!clearance) return null;
  const isDirector = !!clearance.is_director;
  const color = isDirector ? desk.gold : desk.classified;
  const stars = isDirector ? 5 : clearance.rank;
  return (
    <div
      className="border px-3 py-2 text-right"
      style={{ borderColor: color + "55", backgroundColor: "rgba(0,0,0,0.3)" }}
      data-testid="desk-clearance-emblem"
    >
      <div className={fontMicro} style={{ color: desk.inkMuted }}>
        ARCHIVE CLEARANCE
      </div>
      <div className="flex items-center justify-end gap-1.5 mt-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            size={10}
            fill={i <= stars ? color : "transparent"}
            color={color}
            strokeWidth={1.5}
          />
        ))}
      </div>
      <div className="font-display text-base mt-1" style={{ color }}>
        {clearance.label}
      </div>
    </div>
  );
};

export default DeskHeader;
